import { FinancialYearValues } from "@impero-com/common/components/financialYear/editor";
import { t } from "@impero-com/common/utils/i18n";
import {
    notifyRequestError,
    STALE_TIME_RESOURCES,
} from "@impero-com/common/utils/query_client";
import { emitNotification } from "@impero-com/ui";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";

import {
    useDeleteFinancialYear,
    getFinancialYear,
    usePostFinancialYear,
    usePutFinancialYear,
} from "api/generated/financial-years/financial-years";
import { FinancialYearData, FinancialYearView } from "api/generated/model";
import { FINANCIAL_YEAR_QUERY_KEY } from "org/control/view/queries/useFinancialYearQuery";

const queryKey = [FINANCIAL_YEAR_QUERY_KEY];

export function financialYearToFormValues(
    financialYear: FinancialYearView
): FinancialYearValues {
    const startDate = new Date();
    startDate.setMonth(financialYear.startMonth - 1);
    startDate.setDate(financialYear.startDay);
    return { startDate };
}

export function useFinancialYearEditor() {
    const queryClient = useQueryClient();
    const { data: financialYear } = useSuspenseQuery({
        queryKey,
        queryFn: async () => (await getFinancialYear()).data,
        staleTime: STALE_TIME_RESOURCES,
    });
    const create = usePostFinancialYear({
        mutation: {
            onSuccess: () => {
                emitNotification("success", t("Financial_Year_Created"));
                void queryClient.invalidateQueries({ queryKey });
            },
            onError: () =>
                emitNotification("error", t("Financial_Year_AlreadyExists")),
        },
    });
    const update = usePutFinancialYear({
        mutation: {
            onSuccess: () => {
                emitNotification("success", t("Financial_Year_Updated"));
                void queryClient.invalidateQueries({ queryKey });
            },
            onError: () =>
                emitNotification("error", t("Financial_Year_DoesNotExist")),
        },
    });
    const remove = useDeleteFinancialYear({
        mutation: {
            onSuccess: () => {
                emitNotification("success", t("Financial_Year_Deleted"));
                void queryClient.invalidateQueries({ queryKey });
            },
            onError: (error) => {
                if (error.response?.status === 409) {
                    emitNotification("error", t("Financial_Year_DoesNotExist"));
                    return;
                }
                notifyRequestError(error);
            },
        },
    });
    const onSave = ({ startDate }: FinancialYearValues) => {
        const data: FinancialYearData = {
            startDay: startDate.getDate(),
            startMonth: startDate.getMonth() + 1,
        };
        return financialYear
            ? update.mutateAsync({ data })
            : create.mutateAsync({ data });
    };
    return {
        financialYear,
        onSave,
        onDelete: () => remove.mutateAsync(),
        isSaving: create.isPending || update.isPending,
        isDeleting: remove.isPending,
    };
}
